import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {SpinnerService} from '../shared/services/spinner.service';
import {BusinessDirectoryService} from '../api/business-directory.service';
import {BusinessDirectory} from '../models/business-directory';
import {BUSINESS_CATEGORIES_GROUPED} from '../constants';
import {UserApiService} from '../api/user-api.service';
import {forkJoin} from 'rxjs';
import {map} from 'rxjs/operators';

@Component({
    selector: 'app-biz-directory',
    templateUrl: './biz-directory.component.html',
    styleUrls: ['./biz-directory.component.css']
})


export class BizDirectoryComponent implements OnInit {

    userRole: any;
    bizForm!: FormGroup;
    businesses: BusinessDirectory[] = [];
    selectedBusiness: BusinessDirectory | null = null;
    viewMode: 'list' | 'form' | 'detail' = 'list';
    isEditing = false;
    loading = false;
    businessCategories = BUSINESS_CATEGORIES_GROUPED;
    userId = sessionStorage.getItem('userId') || '';
    page: number = 1;
    pageSize: number = 10;

    constructor(
        private fb: FormBuilder,
        private bizService: BusinessDirectoryService,
        private toastr: ToastrService,
        private spinner: SpinnerService,
        private userApiService: UserApiService,
    ) {
    }

    ngOnInit(): void {
        this.initForm();
        this.loadBusinesses();
        this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');

    }

    private initForm(): void {
        this.bizForm = this.fb.group({
            id: [''],
            createdBy: [this.userId],
            createdByName: [''],       // auto-set
            businessName: ['', Validators.required],
            industry: ['', Validators.required],
            description: [''],
            website: ['', [Validators.pattern('^(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?$')]],
            logoUrl: [''],
            visible: [true],
            createdDate: [''],
            updatedBy: [''],
            updatedDate: ['']
        });
    }


    loadBusinesses(): void {
        this.loading = true;

        this.bizService.getBusinesses().subscribe({
            next: (data) => {
                const uid = this.userId;

                // Put current user's items first
                const mine = data.filter(b => b.createdBy === uid);
                const others = data.filter(b => b.createdBy !== uid);

                // Optional: sort each group by createdDate descending
                mine.sort((a, b) => (b.createdDate || '').localeCompare(a.createdDate || ''));
                others.sort((a, b) => (b.createdDate || '').localeCompare(a.createdDate || ''));

                // Combine lists
                const allBusinesses = [...mine, ...others];

                // Enrich each business with createdByName
                const requests = allBusinesses.map(biz =>
                    this.userApiService.getShortData(biz.createdBy).pipe(
                        map(user => ({
                            ...biz,
                            createdByName: user.firstname + ' ' + user.lastname
                        }))
                    )
                );

                // Execute all requests in parallel
                forkJoin(requests).subscribe({
                    next: (enrichedBusinesses) => {
                        this.businesses = enrichedBusinesses;
                        this.loading = false;
                    },
                    error: (err) => {
                        console.error('Error fetching user info:', err);
                        this.businesses = allBusinesses; // fallback without names
                        this.loading = false;
                    }
                });
            },
            error: () => {
                this.toastr.error('Failed to load businesses.');
                this.loading = false;
            }
        });
    }


    openCreateForm(): void {
        this.resetForm();
        this.viewMode = 'form';
    }

    backToList(): void {
        this.resetForm();
        this.viewMode = 'list';
    }

    onSubmit(): void {
        if (this.bizForm.invalid) {
            this.toastr.error('Please fill all required fields');
            return;
        }
        //const rawDesc = this.bizForm.get('description')?.value;
        ////const htmlDesc = rawDesc ? toHTML(rawDesc) : '';


        // inside onSubmit
        const rawDesc = this.bizForm.get('description')?.value;

        let htmlDesc = '';
        if (rawDesc) {
            // description is always an HTML string from app-rt-editor
            htmlDesc = typeof rawDesc === 'string' ? rawDesc : '';
        }


        const biz: BusinessDirectory = {
            ...this.bizForm.value,
            description: htmlDesc, // always HTML
            createdBy: this.userId
        };
        if (this.isEditing && biz.id) {
            this.bizService.updateBusiness(biz.id, biz).subscribe({
                next: () => {
                    this.loadBusinesses();
                    this.toastr.success('Business updated successfully!');
                    this.backToList();
                },
                error: () => this.toastr.error('Failed to update business')
            });
        } else {
            this.bizService.createBusiness(biz).subscribe({
                next: () => {
                    this.loadBusinesses();
                    this.toastr.success('Business added successfully!');
                    this.backToList();
                },
                error: () => this.toastr.error('Failed to add business')
            });
        }
    }

    editBusiness(biz: BusinessDirectory) {
        this.isEditing = true;
        this.viewMode = 'form';
        this.bizForm.patchValue(biz);
    }

    viewBusiness(biz: BusinessDirectory) {
        this.selectedBusiness = biz;
        this.viewMode = 'detail';
    }

    deleteBusiness(id?: string) {
        if (!id) return;
        if (!confirm('Are you sure?')) return;

        this.bizService.deleteBusiness(id).subscribe({
            next: () => {
                this.toastr.success('Deleted successfully!');
                this.loadBusinesses();
                this.backToList();
            },
            error: () => this.toastr.error('Failed to delete business')
        });
    }

    resetForm() {
        this.bizForm.reset({createdBy: this.userId, visible: true});
        this.isEditing = false;
        this.selectedBusiness = null;
    }


    bizInitials(name: string | undefined): string {
        const parts = (name || '').trim().split(/\s+/).filter(p => p);
        const initials = (parts[0]?.charAt(0) || '') + (parts[1]?.charAt(0) || '');
        return initials.toUpperCase() || '?';
    }

    getCategoryName(code: string | undefined): string {
        if (!code) return '';
        for (let group of this.businessCategories) {
            const found = group.categories.find(cat => cat.code === code);
            if (found) return found.name;
        }
        return code; // fallback to code if not found
    }

}
